import { useState, useEffect, useRef } from "react";

export default function Contact() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const section = sectionRef.current;
    if (section) {
      const elements = section.querySelectorAll(".reveal");
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const url = form.getAttribute("action") || "";

    fetch(url, {
      method: "POST",
      body: new URLSearchParams(formData as never),
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    })
      .then(() => {
        setFormStatus("success");
        form.reset();
      })
      .catch(() => {
        setFormStatus("error");
      });
  };

  return (
    <section id="contact" ref={sectionRef} className="py-20 md:py-28 lg:py-32 bg-background-50">
      <div className="px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 md:mb-20">
            <span className="reveal text-accent-500 text-xs font-semibold tracking-[0.25em] uppercase mb-4 block">
              Get in Touch
            </span>
            <h2 className="reveal font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
              Request Your <span className="text-primary-600">Security Assessment</span>
            </h2>
            <p className="reveal text-foreground-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Get in touch with our security experts for a free, no-obligation consultation and risk assessment.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Form */}
            <div className="reveal">
              <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-6">
                Send Us a Message
              </h3>

              {formStatus === "success" && (
                <div className="mb-6 p-4 bg-primary-100 border border-primary-200 rounded-lg text-primary-700 text-sm">
                  <i className="ri-check-line mr-2" />
                  Thank you! Your message has been sent. We will respond within 24 hours.
                </div>
              )}

              {formStatus === "error" && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <i className="ri-error-warning-line mr-2" />
                  Something went wrong. Please try again or call us directly.
                </div>
              )}

              <form
                id="contact-form"
                data-readdy-form="contact"
                action="/contact.php"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      placeholder="John Doe"
                      className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="john@company.com"
                      className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      placeholder="(310) 555-0199"
                      className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="inquiryType" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Inquiry Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="inquiryType"
                      name="inquiryType"
                      required
                      className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    >
                      <option value="">Select an option</option>
                      <option value="Service Request">Service Request</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Employment">Employment</option>
                      <option value="Emergency">Emergency</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us about your security needs..."
                    className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                  />
                  <div className="text-xs text-foreground-400 mt-1 text-right">
                    Max 500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold px-8 py-4 rounded-md transition-all duration-300 whitespace-nowrap"
                >
                  Send Message
                  <i className="ri-send-plane-line" />
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="reveal space-y-8">
              <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-6">
                Contact Information
              </h3>

              <div className="text-xs text-accent-500 font-semibold tracking-[0.2em] uppercase mb-2">
                Contact us 24/7 by phone, fax or email.
              </div>

              {/* Office Contact Card */}
              <div className="bg-background-100 rounded-xl p-5 border border-background-200">
                <h4 className="font-heading text-base font-bold text-foreground-950 mb-4">Office</h4>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-lg">
                      <i className="ri-building-line text-primary-600 text-lg" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground-950 text-sm">Askari Security Services, Inc.</p>
                      <p className="text-foreground-600 text-sm">
                        5150 Candlewood Street, Suite 16H
                        <br />
                        Lakewood, CA 90712
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-accent-100 rounded-lg">
                      <i className="ri-phone-line text-accent-600 text-lg" />
                    </div>
                    <div>
                      <p className="text-foreground-600 text-sm">
                        Tel: <a href="tel:+15626060053" className="text-primary-600 hover:text-accent-500 transition-colors">(562) 606-0053</a>
                      </p>
                      <p className="text-foreground-500 text-sm">
                        Fax: (562) 606-0098
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-secondary-100 rounded-lg">
                      <i className="ri-mail-line text-secondary-600 text-lg" />
                    </div>
                    <div>
                      <p className="text-foreground-600 text-sm">
                        <a href="mailto:office@askari.org" className="text-primary-600 hover:text-accent-500 transition-colors">
                          office@askari.org
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Director Contact Card */}
              <div className="bg-background-100 rounded-xl p-5 border border-background-200">
                <h4 className="font-heading text-base font-bold text-foreground-950 mb-4">Director</h4>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-lg">
                      <i className="ri-user-star-line text-primary-600 text-lg" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground-950 text-sm">Michael N. Karanja, C.E.O.</p>
                      <p className="text-foreground-600 text-sm">
                        5150 Candlewood Street, Suite 16H
                        <br />
                        Lakewood, CA 90712
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-accent-100 rounded-lg">
                      <i className="ri-smartphone-line text-accent-600 text-lg" />
                    </div>
                    <div>
                      <p className="text-foreground-600 text-sm">
                        Cell: <a href="tel:+13232412337" className="text-primary-600 hover:text-accent-500 transition-colors">(323) 241-2337</a>
                      </p>
                      <p className="text-foreground-500 text-sm">
                        Fax: (562) 606-0098
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-secondary-100 rounded-lg">
                      <i className="ri-mail-line text-secondary-600 text-lg" />
                    </div>
                    <div>
                      <p className="text-foreground-600 text-sm">
                        <a href="mailto:michael.karanja@askari.org" className="text-primary-600 hover:text-accent-500 transition-colors">
                          michael.karanja@askari.org
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Hours Card */}
              <div className="bg-background-100 rounded-xl p-5 border border-background-200">
                <div className="flex gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-lg">
                    <i className="ri-time-line text-primary-600 text-lg" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground-950 text-sm mb-1">Business Hours</h4>
                    <p className="text-foreground-600 text-sm">Monday - Friday: 8:00 AM - 6:00 PM</p>
                    <p className="text-foreground-500 text-xs">Emergency Response: 24/7/365</p>
                  </div>
                </div>
              </div>

              {/* Quick Response CTA */}
              <div className="bg-primary-50 rounded-xl p-6 border border-primary-200">
                <h4 className="font-heading text-lg font-bold text-primary-700 mb-2">
                  Quick Response Team
                </h4>
                <p className="text-foreground-600 text-sm mb-4">
                  For immediate security concerns or emergency situations, our 24/7 response team is always ready to assist you.
                </p>
                <a
                  href="tel:+15626060053"
                  className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold px-6 py-3 rounded-md transition-all duration-300 whitespace-nowrap text-sm"
                >
                  <i className="ri-phone-line" />
                  Emergency Contact
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}